// Pure composition rules for admin extension slots and action menus (no React
// rendering, no network): first-party cards (registry.ts) always come first,
// in their registered order, then installed plugins' extensions in the order
// the server returns them (plugin name, then manifest order).

import type { AdminTarget } from '@dunea/plugin-sdk';
import type { AdminExtensionView } from '@dunea/shared';
import type { FirstPartyAdminCard } from './registry';

/** Mirrors adminExtensionKind in @dunea/plugin-sdk primitives/admin. */
export function adminTargetKind(target: AdminTarget): 'block' | 'action' {
  return /actions$/.test(target) ? 'action' : 'block';
}

export type SlotEntry =
  | { source: 'first-party'; key: string; title: string; card: FirstPartyAdminCard }
  | { source: 'plugin'; key: string; title: string; extension: AdminExtensionView };

export function extensionKey(x: Pick<AdminExtensionView, 'installId' | 'index'>): string {
  return `plugin:${x.installId}:${x.index}`;
}

/**
 * Everything a slot (blocks) or menu (actions) shows for `target`. Plugin
 * extensions for a different target or of the wrong kind are dropped, so a
 * stale or mismatched list can't leak into the wrong view.
 */
export function composeSlot(
  target: AdminTarget,
  firstParty: readonly FirstPartyAdminCard[],
  plugins: readonly AdminExtensionView[] | null | undefined,
): SlotEntry[] {
  const kind = adminTargetKind(target);
  const seen = new Set<string>();
  const out: SlotEntry[] = [];

  for (const card of firstParty) {
    const key = `first-party:${card.id}`;

    if (!seen.has(key)) {
      seen.add(key);
      out.push({ source: 'first-party', key, title: card.title, card });
    }
  }

  for (const extension of plugins ?? []) {
    const key = extensionKey(extension);

    if (extension.target !== target || extension.kind !== kind || seen.has(key)) {
      continue;
    }

    seen.add(key);
    out.push({ source: 'plugin', key, title: extension.title, extension });
  }

  return out;
}

export const BLOCK_MIN_HEIGHT = 60;
/** Blocks sit inside resource views: they may grow, but not take over the panel. */
export const BLOCK_MAX_HEIGHT = 800;

/** A block's frame height: the manifest's initial height, then what the frame asks for, clamped. */
export function blockHeight(manifestHeight: number, requested: number | null): number {
  const h = requested ?? manifestHeight;

  if (!Number.isFinite(h)) {
    return BLOCK_MIN_HEIGHT;
  }

  return Math.max(BLOCK_MIN_HEIGHT, Math.min(BLOCK_MAX_HEIGHT, Math.round(h)));
}

/** The session resourceId for a bulk target: unique ids joined by ",", or null when none are selected. */
export function bulkResourceId(ids: readonly string[], max = 100): string | null {
  const unique = [...new Set(ids.filter(Boolean))];

  return unique.length === 0 || unique.length > max ? null : unique.join(',');
}
